// §4.1's promises tile (spec 03 §4.1, P3-J J-3, D-466): the facility's
// promises at risk and those broken in the last 30 days, each linking to the
// list filtered by that status, and the next promised dates within 7 days,
// linking to the list over that week. Every `planning.read` holder sees it,
// because the promises list is read on that code. Loading: tile skeletons.
// Error: the request id and Try again. On a phone the tiles stack.

import { useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { ErrorState } from "../../../../components/states";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { usePromiseTile } from "../hooks/promise-queries";
import { formatDate } from "../lib/planning-lib";
import { usePlanningFacility } from "../planning-layout";

/** How many of the next promised dates the tile names. */
export const UPCOMING_SHOWN = 3;

export function PromisesWorkbenchSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const now = Date.now();
  const today = facilityTodayIso(now, facility.timezone);
  const tile = usePromiseTile(facility.id, today, now);

  useEffect(() => {
    if (tile.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [tile.error, navigate]);

  const head = <h2 id="planning-promises-h">{t("planning.promises.tile.title")}</h2>;
  if (tile.error !== null) {
    return (
      <section
        className="planning-mrp"
        aria-labelledby="planning-promises-h"
        data-testid="planning-promises-section"
      >
        <div className="planning-mrp-head">{head}</div>
        <ErrorState
          title={t("planning.promises.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void tile.refetch()}
          requestId={requestIdOf(tile.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const facts = tile.data;
  const loading = tile.isPending;
  const count = (n: number, more: boolean) =>
    more ? t("planning.promises.tile.atLeast", { count: n }) : String(n);
  const weekOn = addDaysIso(today, 7);
  const upcoming = facts?.upcoming ?? [];

  return (
    <section
      className="planning-mrp"
      aria-labelledby="planning-promises-h"
      data-testid="planning-promises-section"
    >
      <div className="planning-mrp-head">{head}</div>
      <StatStrip data-testid="planning-promise-tiles">
        <StatTile
          label={t("planning.promises.tile.atRisk")}
          loading={loading}
          data-testid="planning-tile-promises-at-risk"
          data-count={facts?.atRisk}
          value={
            <Link
              to="/planning/promises"
              search={{ status: "at_risk" }}
              className="planning-mps-tile-link"
            >
              {count(facts?.atRisk ?? 0, facts?.atRiskMore ?? false)}
            </Link>
          }
          sub={
            facts?.atRisk === 0 ? (
              <span data-testid="planning-tile-promises-none">
                {t("planning.promises.tile.empty")}
              </span>
            ) : undefined
          }
        />
        <StatTile
          label={t("planning.promises.tile.broken")}
          loading={loading}
          data-testid="planning-tile-promises-broken"
          data-count={facts?.broken}
          value={
            <Link
              to="/planning/promises"
              search={{ status: "broken" }}
              className="planning-mps-tile-link"
            >
              {count(facts?.broken ?? 0, facts?.brokenMore ?? false)}
            </Link>
          }
        />
        <StatTile
          label={t("planning.promises.tile.upcoming")}
          loading={loading}
          data-testid="planning-tile-promises-upcoming"
          data-count={upcoming.length}
          value={
            <Link
              to="/planning/promises"
              search={{ from: today, to: weekOn }}
              className="planning-mps-tile-link"
            >
              {count(upcoming.length, facts?.upcomingMore ?? false)}
            </Link>
          }
          sub={
            facts === undefined ? undefined : upcoming.length === 0 ? (
              t("planning.promises.tile.upcomingNone")
            ) : (
              <span className="planning-tile-counts">
                {upcoming.slice(0, UPCOMING_SHOWN).map((p) => (
                  <Link
                    key={p.id}
                    to="/planning/promises"
                    search={{ promise: p.id }}
                    data-testid="planning-tile-promise-next"
                    data-promise-id={p.id}
                    data-date={p.promised_date}
                  >
                    {t("planning.promises.tile.next", {
                      number: p.promise_no,
                      date: formatDate(p.promised_date, i18n.language),
                    })}
                  </Link>
                ))}
              </span>
            )
          }
        />
      </StatStrip>
    </section>
  );
}
